import React, { useState } from 'react';
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Button from "@amzn/meridian/button";
import Tooltip from "@amzn/meridian/tooltip";
import arrowUpSmallTokens from "@amzn/meridian-tokens/base/icon/arrow-up-small";
import arrowDownSmallTokens from "@amzn/meridian-tokens/base/icon/arrow-down-small";
import infoTokens from "@amzn/meridian-tokens/base/icon/info";
import './index.scss';

export interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  subtitle?: string;
  valueDiff?: number;
  subtext?: string;
  subtextColor?: 'error' | 'success' | 'warning' | 'secondary';
  valueColor?: 'primary' | 'error' | 'success';
  titleColor?: 'primary' | 'secondary';
  tooltip?: string;
  icon?: React.ReactNode;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

/**
 * MetricCard component using Meridian color tokens via component props.
 * 
 * valueColor mapping:
 *   - "primary" → theme palette color (purple) via Heading color="primary"
 *   - "error" → red via Text color="error" 
 *   - "success" → green via Text color="success"
 *   - undefined → default ink color
 */
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit,
  subtitle,
  valueDiff,
  subtext,
  subtextColor = 'secondary',
  valueColor,
  titleColor,
  tooltip,
  icon,
  className = '',
  size = 'medium',
}) => {
  /**
   * Renders the value using appropriate Meridian component with semantic color.
   * Heading supports: "primary" | "secondary"
   * Text supports: "primary" | "secondary" | "error" | "warning" | "success" | "inverted"
   */
  const renderValue = () => {
    if (valueColor === 'primary') {
      // Theme palette color via Heading's color prop
      return (
        <Heading level={1} color="primary">
          {value}
        </Heading>
      );
    }
    if (valueColor === 'error' || valueColor === 'success') {
      // Error/success colors via Text component (Heading doesn't support these)
      return (
        <Text type="d100" color={valueColor}>
          {value}
        </Text>
      );
    }
    // Default: no specific color override
    return (
      <Heading level={1}>
        {value}
      </Heading>
    );
  };

  return (
    <Box
      type="outline"
      className={`metric-card metric-card--${size} ${className}`}
      spacingInset="350"
    >
      <Row>
        {icon && (
          <Box className="metric-card__icon">
            {icon}
          </Box>
        )}

        <Box className="metric-card__content">
          <Row alignmentVertical="center" spacing="100" widths={["fill", "fit"]}>
            <Text
              className="metric-card-title"
              color={titleColor || "secondary"}
              type="h100"
              alignment="left"
            >
              {title}
            </Text>

            {tooltip && (
              <Tooltip
                title={tooltip}
                position="top"
                maxWidth={240}
              >
                <Button type="icon" size="small" label={`Info: ${title}`}>
                  <Icon tokens={infoTokens} />
                </Button>
              </Tooltip>
            )}
          </Row>

          {subtitle && (
            <Text
              className="metric-card-title"
              color={titleColor || "secondary"}
              type="h100"
              alignment="left"
            >
              {subtitle}
            </Text>
          )}

          <Box className="metric-card__value">
            {renderValue()}
            {unit && (
              <Text type="h100" className="metric-card__unit">
                {unit}
              </Text>
            )}
          </Box>

          {subtext && valueDiff && (
            <div className="metric-card__subtitle">
              <Icon tokens={valueDiff>=0 ? arrowUpSmallTokens : arrowDownSmallTokens} color={subtextColor} />
              <Text color={subtextColor} type="h100" >
                {Math.abs(valueDiff)} {subtext}
              </Text>
            </div>
          )}
        </Box>
      </Row>
    </Box>
  );
};

export default MetricCard;

import './index.scss';